class JewelryShowcase extends HTMLElement {
  constructor() {
    super();
    this.cards = [];
    this.offset = 0;
    this.animating = false;
    this.NUM_POSITIONS = 5;
    this.CENTER_POS = 3;
    this.ANIMATION_MS = 600;
  }

  connectedCallback() {
    this.cards = Array.from(this.querySelectorAll('.showcase__card[data-card-index]'));
    this.prevBtn = this.querySelector('[data-showcase-nav="prev"]');
    this.nextBtn = this.querySelector('[data-showcase-nav="next"]');

    this.bindEvents();
    this.applyPositions();
    this.playCenterVideo();
  }

  bindEvents() {
    if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.move(-1));
    if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.move(1));

    this.cards.forEach((card) => {
      const handler = (event) => {
        const pos = parseInt(card.dataset.pos || '0', 10);
        if (pos === 0) return;
        if (pos === this.CENTER_POS) {
          const link = card.dataset.link || '';
          if (link) window.location.href = link;
          return;
        }
        event.preventDefault();
        this.move(pos - this.CENTER_POS);
      };
      card.addEventListener('click', handler);
      card.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') handler(event);
      });
    });

    this.addEventListener('keydown', (event) => {
      if (event.target.closest('.showcase__card')) return;
      if (event.key === 'ArrowLeft') { event.preventDefault(); this.move(-1); }
      else if (event.key === 'ArrowRight') { event.preventDefault(); this.move(1); }
    });

    let touchStartX = 0;
    let touchStartY = 0;
    const composite = this.querySelector('.showcase__composite');
    if (composite) {
      composite.addEventListener('touchstart', (e) => {
        touchStartX = e.changedTouches[0].clientX;
        touchStartY = e.changedTouches[0].clientY;
      }, { passive: true });
      composite.addEventListener('touchend', (e) => {
        const dx = e.changedTouches[0].clientX - touchStartX;
        const dy = e.changedTouches[0].clientY - touchStartY;
        if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) {
          this.move(dx < 0 ? 1 : -1);
        }
      }, { passive: true });
    }
  }

  posForIndex(idx, offset) {
    const n = this.cards.length;
    if (n === 0) return 0;
    const rotated = ((idx - offset) % n + n) % n;
    return rotated < this.NUM_POSITIONS ? rotated + 1 : 0;
  }

  move(delta) {
    if (!delta || this.animating || this.cards.length === 0) return;
    this.animating = true;
    this.classList.add('is-animating');

    // Pause any video that's currently playing; keep the current frame visible.
    this.cards.forEach((card) => {
      const v = card.querySelector('video');
      if (v) { try { v.pause(); } catch (e) { /* ignore */ } }
    });

    const n = this.cards.length;
    const oldOffset = this.offset;
    const newOffset = ((this.offset + delta) % n + n) % n;

    // Tag cards whose visible position jumps more than one slot — those
    // need to fade out/in so the user doesn't see them fly across.
    this.cards.forEach((card, idx) => {
      const oldPos = this.posForIndex(idx, oldOffset);
      const newPos = this.posForIndex(idx, newOffset);
      const bothVisible = oldPos > 0 && newPos > 0;
      const farJump = bothVisible && Math.abs(newPos - oldPos) > 1;
      if (farJump) card.classList.add('is-wrapping');
    });

    // Commit the new offset and push data-pos into the DOM; CSS handles
    // the actual slide.
    this.offset = newOffset;
    this.applyPositions();

    window.setTimeout(() => {
      this.cards.forEach((card) => card.classList.remove('is-wrapping'));
      this.classList.remove('is-animating');
      this.animating = false;
      this.playCenterVideo();
    }, this.ANIMATION_MS);
  }

  applyPositions() {
    this.cards.forEach((card, idx) => {
      const pos = this.posForIndex(idx, this.offset);
      card.dataset.pos = String(pos);
      card.classList.toggle('is-center', pos === this.CENTER_POS);
    });
  }

  playCenterVideo() {
    this.cards.forEach((card) => {
      const video = card.querySelector('video.showcase__card-video');
      if (!video) return;
      if (card.classList.contains('is-center')) {
        video.currentTime = 0;
        const p = video.play();
        if (p && typeof p.catch === 'function') p.catch(() => {});
      } else {
        video.pause();
      }
    });
  }
}

if (!customElements.get('jewelry-showcase')) {
  customElements.define('jewelry-showcase', JewelryShowcase);
}
